import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, FormControl, InputLabel, Select, MenuItem,
    Alert, CircularProgress, Switch, FormControlLabel
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ReservationService } from '../api/services/reservationService';
import { convertCsvToExportFormat } from '../utils/exportUtils';

export default function ExportReservationModal({ open, onClose, tenantId, currentFilters, setNotification }) {
    const [format, setFormat] = useState('csv');
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setFormat('csv');
            setError(null);
        }
    }, [open]);

    const handleExport = async () => {
        setIsExporting(true);
        setError(null);
        
        try {
            const params = {
                ...currentFilters,
                format: format
            };

            // Clean up any undefined or empty string values from filters just in case
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === 'All' || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await ReservationService.exportReport(params, tenantId);
            
            const successMsg = response?.data?.message || response?.message || 'Export started. You will receive an email shortly.';
            
            if (setNotification) {
                setNotification({ open: true, message: successMsg, severity: 'success' });
            }
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to generate and download report';
            if (setNotification) {
                setNotification({ open: true, message: msg, severity: 'error' });
            } else {
                setError(msg);
            }
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={isExporting ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <FileDownloadOutlinedIcon color="primary" /> Export Reservation Report
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>File Format</InputLabel>
                        <Select
                            value={format}
                            label="File Format"
                            onChange={(e) => setFormat(e.target.value)}
                            disabled={isExporting}
                        >
                            <MenuItem value="csv">CSV</MenuItem>
                            <MenuItem value="excel">Excel</MenuItem>
                            <MenuItem value="pdf">PDF</MenuItem>
                        </Select>
                    </FormControl>

                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isExporting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleExport}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                    disabled={isExporting}
                >
                    {isExporting ? <CircularProgress size={20} color="inherit" /> : 'Download Report'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
